/**
 * Created by Daniel Wang on 04/20/2022.
 * Description: ExchangeImportDuplicateDialog Component
 * ------ maintenance history ------
 */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';
import { of as observableOf } from 'rxjs';

import { ExchangeDataContainer, DuplicateItem, ExchangeImportDetailDataModel, IntegratedValue, ExchangeReturnDataModel, NeedReturnContact, ExchangeContactDetail, JobTitle } from '../../../tamalelibs/models/exchange-import.model';
import { ExchangeService } from '../../../tamalelibs/services/exchange.service';
import { ToastService } from '../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { DialogAction, DialogService, DialogType } from '../../../services/dialog.service';
import { UtilsService } from '../../../tamalelibs/services/utils.service';
import { businessConstants } from '../../../tamalelibs/constants/business.constants';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';
import { ExchangeImportListDialogService } from '../exchange-import-list-dialog/exchange-import-list-dialog.service';
import { ExchangeImportCheckDialogService } from '../exchange-import-check-dialog/exchange-import-check-dialog.service';
import { ArrayHelperService } from '../../../tamalelibs/services/array-helper.service';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';

@Component({
    selector: 'tam-exchange-import-duplicate-dialog',
    templateUrl: './exchange-import-duplicate-dialog.component.html',
    styleUrls: ['./exchange-import-duplicate-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExchangeImportDuplicateDialogComponent implements OnInit, OnDestroy {
    //#region public properties
    dialogWidth = 710;
    duplicateData: Array<ExchangeDataContainer>;
    isInit = false;
    isOpened: boolean;
    isProgressing = false;
    needCreateNewContact: Array<NeedReturnContact>;
    originalData: ExchangeImportDetailDataModel;
    sameData: Array<any>;
    titleName = StringLiteralsPipe.translate('exchange.import_contacts');
    //#endregion

    //#region private properties

    /**
    * the subscription array that would be unsubscribed on destroying.
    */
    private _destroySubscriptions: Array<Subscription> = [];

    //#endregion

    constructor(
        private _alertWindow: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _dialogService: DialogService,
        private _exchangeImportCheckDialogService: ExchangeImportCheckDialogService,
        private _exchangeImportListDialogService: ExchangeImportListDialogService,
        private _exchangeService: ExchangeService,
        private _toast: ToastService,
        private _utilsService: UtilsService) { }

    //#region public methods

    ngOnInit() {
        this._destroySubscriptions.push(
            this._dialogService.dialogOpen$.pipe(
                filter(item => item.type === DialogType.ExchangeDuplicateDialog),
            ).subscribe((data) => {
                this.isOpened = true;
                this.isInit = true;
                // order by the duplicate items
                data.payload.duplicateData.forEach(item => {
                    return item.duplicates.sort((a, b) => {
                        return this._utilsService.sort(a.orderBy, b.orderBy);
                    });
                });

                this.duplicateData = data.payload.duplicateData;
                this.needCreateNewContact = data.payload.needCreateNewContact;
                this.originalData = data.payload.originalData;
                this._changeDetectorRef.detectChanges();
            }),
            this._dialogService.dialogClose$.subscribe(action => {
                if (action.type === DialogType.ExchangeDuplicateDialog) {
                    this.isOpened = false;
                }
            }));
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    onCancelBtnClick() {
        this.isOpened = false;
        this.isInit = false;
        this._changeDetectorRef.detectChanges();
    }

    onCloseBtnClick() {
        const subscription = this._alertWindow.warn(StringLiteralsPipe.translate('general.warning'),
            [StringLiteralsPipe.translate('general.are_you_sure_you_want_to_close_the_dialog')], StringLiteralsPipe.translate('general.close')).subscribe((result: boolean) => {
                if (result) {
                    this.isOpened = false;
                    this.isInit = false;
                    this._closeDialog();
                    this._changeDetectorRef.detectChanges();
                }
                subscription.unsubscribe();
            });
    }

    /**
     *
     * @param {*} item
     * @memberof ExchangeImportDuplicateDialogComponent
     */
    onClickMergeAll(item) {
        item.isMergedAll = true;

        item.duplicates.forEach(data => {
            data.isMergedItem = true;
        });
        this._changeDetectorRef.detectChanges();
    }

    /**
     *
     * @param {*} item
     * @memberof ExchangeImportDuplicateDialogComponent
     */
    onClickMergeItem(item: DuplicateItem) {
        item.isMergedItem = true;

        let isAllMerged = false;
        this.duplicateData.forEach(data => {
            isAllMerged = true;
            data.duplicates.forEach(duplicate => {
                if (!duplicate.isMergedItem) {
                    isAllMerged = false;
                }
            });
            data.isMergedAll = isAllMerged;
        });
        this._changeDetectorRef.detectChanges();
    }

    /**
     *
     * Click Undo will revert status of merge all.
     * @param {*} item
     * @memberof ExchangeImportDuplicateDialogComponent
     */
    onClickUndoMerge(item) {
        item.isMergedAll = false;

        item.duplicates.forEach(data => {
            data.isMergedItem = false;
        });
        this._changeDetectorRef.detectChanges();
    }

    /**
     *
     * Click Undo will revert status of merge item.
     * @param {*} item
     * @memberof ExchangeImportDuplicateDialogComponent
     */
    onClickUndoMergeItem(item: DuplicateItem) {
        item.isMergedItem = false;

        let isAllMerged = false;
        this.duplicateData.forEach(data => {
            isAllMerged = false;
            data.duplicates.forEach(duplicate => {
                if (!duplicate.isMergedItem) {
                    isAllMerged = true;
                }
            });
            data.isMergedAll = !isAllMerged;
        });
        this._changeDetectorRef.detectChanges();
    }

    /**
     * Logic for selected duplicate item.
     *
     * @param {*} item
     * @memberof ExchangeImportDuplicateDialogComponent
     */
    onClickExchangeItem(item) {
        item.isExchangeValueSelected = !item.isExchangeValueSelected;
        if (item.isExchangeValueSelected) {
            item.isTamaleValueSelected = !item.isExchangeValueSelected;
        }
        this._changeDetectorRef.detectChanges();
    }

    /**
     * Logic for selected duplicate item.
     *
     * @param {*} item
     * @memberof ExchangeImportDuplicateDialogComponent
     */
    onClickTamaleItem(item) {
        item.isTamaleValueSelected = !item.isTamaleValueSelected;
        if (item.isTamaleValueSelected) {
            item.isExchangeValueSelected = !item.isTamaleValueSelected;
        }
        this._changeDetectorRef.detectChanges();
    }

    /**
     * Click import button and send import request.
     *
     * @memberof ExchangeImportDuplicateDialogComponent
     */
    onClickImport() {
        this.isProgressing = true;
        this.isInit = false;
        let isExistInvalidItem = false;
        // whether there is an invalid item
        this.duplicateData.forEach(data => {
            data.duplicates.forEach(duplicate => {
                if (!(duplicate.isMergedItem || duplicate.isTamaleValueSelected || duplicate.isExchangeValueSelected)) {
                    isExistInvalidItem = true;
                }
            });
        });
        // there is an invalid item
        if (isExistInvalidItem) {
            this.isProgressing = false;
            this._toast.notify({
                style: NotificationStyles.Error,
                message: StringLiteralsPipe.translate('exchange.please_fix_all_data_conflicts_before_import')
            });
            return;
        } else { // no invlaid item
            // create data for server
            const returnContacts = new Array<NeedReturnContact>();
            this.duplicateData.forEach(data => {
                const contactDetail = new ExchangeContactDetail();
                const tamaleModifyDate = data.tamaleModifyDate;
                const exchangeModifyDate = data.exchangeModifyDate;
                // all items are merged
                if (data.isMergedAll) {
                    data.duplicates.forEach(duplicate => {
                        this._mergeContact(contactDetail, duplicate, tamaleModifyDate, exchangeModifyDate);
                    });
                } else {
                    // not all items are merged
                    data.duplicates.forEach(duplicate => {
                        // handle merged item
                        if (duplicate.isMergedItem) {
                            this._mergeContact(contactDetail, duplicate, tamaleModifyDate, exchangeModifyDate);
                        } else {
                            // handle selected item
                            if (duplicate.isTamaleValueSelected) {
                                this._selectContact(contactDetail, duplicate, true);
                            } else {
                                this._selectContact(contactDetail, duplicate, false);
                            }
                        }
                    });
                }
                // merge the no confliction items to the contctDetail for server
                this._exchangeService.getContactDetailByNoConflictateItems(data.sames, contactDetail);

                const exchange = data.exchange;
                const tamaleId = data.tamaleId;
                const syncType = data.syncType;
                const returnContact = new NeedReturnContact(contactDetail, exchange, tamaleId, true, syncType);
                returnContacts.push(returnContact);
            });

            const exchangeUserId = this.originalData.exchangeUserId;
            const tamaleUserId = this.originalData.tamaleUserId;
            // need to add create contact to the retrun container
            this.needCreateNewContact.forEach(contact => {
                const item = new NeedReturnContact(null, contact.exchange, null, contact.select);
                returnContacts.push(item);
            });
            const returnData = new ExchangeReturnDataModel(returnContacts, exchangeUserId, tamaleUserId);

            this._destroySubscriptions.push(
                this._exchangeService.postExchangeImportContactList(returnData)
                    .pipe(
                        catchError((error) => {
                            this._toast.notify({
                                style: NotificationStyles.Error,
                                message: error.message
                            });
                            this.isProgressing = false;
                            return observableOf(null);
                        }),
                        take(1),
                        filter(res => !!res)
                    )
                    .subscribe(() => {
                        this.isProgressing = false;
                        this._toast.notify({
                            style: NotificationStyles.Info,
                            message: StringLiteralsPipe.translate('general.importing_in_progress')
                        });
                        this._closeDialog();
                        this._changeDetectorRef.detectChanges();
                    })
            );
        }
    }

    // handle the value of display item
    getDisplayItem(data: Array<IntegratedValue>, duplicateItem: DuplicateItem): string {
        return data.map(item => {
            let type = '';
            if (!duplicateItem.duplicateNameShowOnUI.includes(item.name)) {
                type = ' (' + item.name + ')';
            }
            if (typeof item.value === 'object') {
                // to handle address which includes many properties
                return item.value.country + type + '\r\n'
                    + item.value.street + '\r\n'
                    + item.value.street2 + '\r\n'
                    + item.value.city + '\r\n'
                    + item.value.state + '\r\n'
                    + item.value.zip;

            } else {
                if (item.name !== item.value) { // for string, we will set same value for name and value
                    return item.value + type;
                } else {
                    return item.value;
                }
            }
        }).join('\r\n');
    }

    // get duplicate item status for UI
    getDuplicateItemStatus(item: DuplicateItem): boolean {
        const isValid = item.isMergedItem || item.isTamaleValueSelected || item.isExchangeValueSelected || this.isInit;
        return !isValid;
    }

    handleShowName(displayName: string): string {
        let value = displayName;
        if (displayName.includes(businessConstants.exchangeMerge.valueProperty.socialMedia)) {
            value = displayName.substring(displayName.indexOf(' ') + 1);
        }
        return value;
    }

    // need to combine value for notes
    isCombineDuplicateValue(duplicateItem: DuplicateItem): boolean {
        if (duplicateItem.duplicateName === businessConstants.exchangeMerge.fields.notes ||
            duplicateItem.duplicateName === businessConstants.exchangeMerge.fields.jobTitle) {
            return true;
        } else {
            return false;
        }
    }

    // show combine values
    showCombineValue(duplicateItem: DuplicateItem): string {
        let displayCombineValue = '';
        if (duplicateItem.duplicateName === businessConstants.exchangeMerge.fields.notes) {
            displayCombineValue = duplicateItem.tamaleValue[0].value.toString() + '\n' + duplicateItem.exchangeValue[0].value.toString();
        } else if (duplicateItem.duplicateName === businessConstants.exchangeMerge.fields.jobTitle) {
            displayCombineValue = ArrayHelperService.mergeArrayUniqueBy(duplicateItem.tamaleValue, duplicateItem.exchangeValue).map(item => item.value).join('\n');
        }
        return displayCombineValue;
    }

    //#endregion

    //#region private methods
    // close dialog
    private _closeDialog() {
        const dialogAction = new DialogAction(DialogType.ExchangeDuplicateDialog);
        this._dialogService.dialogClose$.next(dialogAction);
        this._exchangeImportListDialogService.dialogClose$.next();
        this._exchangeImportCheckDialogService.dialogClose$.next();
    }
    // merge contact items
    private _mergeContact(contactDetail: ExchangeContactDetail, duplicate: DuplicateItem, tamaleModifyDate, exchangeModifyDate): void {
        // for string type items
        if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.firstName ||
            duplicate.duplicateName === businessConstants.exchangeMerge.fields.lastName ||
            duplicate.duplicateName === businessConstants.exchangeMerge.fields.birthday) {
            if (tamaleModifyDate > exchangeModifyDate) {
                contactDetail[duplicate.duplicateName] = duplicate.tamaleValue[0].name;
            } else {
                contactDetail[duplicate.duplicateName] = duplicate.exchangeValue[0].name;
            }
            // for object type items, only company for now
        } else if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.company) {
            // map the company to web-company
            if (tamaleModifyDate > exchangeModifyDate) {
                contactDetail[duplicate.duplicateName] = {
                    id: duplicate.tamaleValue[0].id,
                    name: duplicate.tamaleValue[0].name
                };
            } else {
                contactDetail[duplicate.duplicateName] = {
                    id: duplicate.exchangeValue[0].id,
                    name: duplicate.exchangeValue[0].name
                };
            }
            // for number type items, only modify data for now
        } else if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.modifyDate) {
            return;
            // for other array type items
        } else {
            const combineContacts = duplicate.tamaleValue.concat(duplicate.exchangeValue);
            if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.alias) {
                contactDetail[duplicate.duplicateName] = combineContacts.map(item => item.name);
                contactDetail[duplicate.duplicateName] = ArrayHelperService.uniqueArray(contactDetail[duplicate.duplicateName]);
            } else if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.jobTitle) {
                combineContacts.forEach(item => {
                    const jobTitle = new JobTitle();
                    jobTitle.id = item.id;
                    jobTitle.name = item.name;
                    if (!contactDetail[duplicate.duplicateName]) {
                        contactDetail[duplicate.duplicateName] = [jobTitle];
                    }
                    if (!ArrayHelperService.arrayContains(contactDetail[duplicate.duplicateName], jobTitle)) {
                        contactDetail[duplicate.duplicateName].push(jobTitle);
                    }
                });
            } else if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.email ||
                duplicate.duplicateName === businessConstants.exchangeMerge.fields.webPhone ||
                duplicate.duplicateName === businessConstants.exchangeMerge.fields.webAddress ||
                duplicate.duplicateName === businessConstants.exchangeMerge.fields.socialMedia) {

                combineContacts.forEach(item => {
                    const contact = {
                        valid: true,
                        name: item.name
                    };
                    const fieldName = duplicate.duplicateName;
                    // add logic to handle different type, since they have different property
                    if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.email) {
                        contact[businessConstants.exchangeMerge.valueProperty.address] = item.value;
                        contact[businessConstants.exchangeMerge.valueProperty.primary] = item.primary;
                    }
                    if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.webPhone) {
                        contact[businessConstants.exchangeMerge.valueProperty.phone] = item.value;
                    }
                    if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.webAddress) {
                        contact[businessConstants.exchangeMerge.fields.id] = item.id;
                        contact[businessConstants.exchangeMerge.valueProperty.address] = item.value;
                    }
                    if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.socialMedia) {
                        contact[businessConstants.exchangeMerge.valueProperty.link] = item.value;
                    }
                    // need to create a new array if it doesn't exist.
                    if (contactDetail[fieldName] === undefined) {
                        contactDetail[fieldName] = [contact];
                    } else {
                        if (!ArrayHelperService.arrayContains(contactDetail[fieldName], contact)) {
                            contactDetail[fieldName].push(contact);
                        }
                    }
                });
                // handle for notes
            } else if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.notes) {
                contactDetail[duplicate.duplicateName] = combineContacts.map(item => item.name).join('\n');
            }
        }
    }

    // select contact item
    private _selectContact(contactDetail: ExchangeContactDetail, duplicate: DuplicateItem, isTamaleContactSelected = false): void {
        let valueProperty = businessConstants.exchangeMerge.valueProperty.tamaleValue;
        if (!isTamaleContactSelected) {
            valueProperty = businessConstants.exchangeMerge.valueProperty.exchangeValue;
        }

        if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.firstName ||
            duplicate.duplicateName === businessConstants.exchangeMerge.fields.lastName ||
            duplicate.duplicateName === businessConstants.exchangeMerge.fields.birthday) {
            contactDetail[duplicate.duplicateName] = duplicate[valueProperty][0].value;
        } else if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.company) {
            contactDetail[duplicate.duplicateName] = {
                id: duplicate[valueProperty][0].id,
                name: duplicate[valueProperty][0].name
            };
        } else if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.modifyDate) {
            return;
        } else {
            if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.alias) {
                contactDetail[duplicate.duplicateName] = duplicate[valueProperty].map(item => item.name);
                contactDetail[duplicate.duplicateName] = ArrayHelperService.uniqueArray(contactDetail[duplicate.duplicateName]);
            } else if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.jobTitle) {
                duplicate[valueProperty].forEach(item => {
                    const jobTitle = new JobTitle();
                    jobTitle.id = item.id;
                    jobTitle.name = item.name;
                    if (!contactDetail[duplicate.duplicateName]) {
                        contactDetail[duplicate.duplicateName] = [];
                    }
                    if (!ArrayHelperService.arrayContains(contactDetail[duplicate.duplicateName], jobTitle)) {
                        contactDetail[duplicate.duplicateName].push(jobTitle);
                    }
                });
            } else if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.email ||
                duplicate.duplicateName === businessConstants.exchangeMerge.fields.webPhone ||
                duplicate.duplicateName === businessConstants.exchangeMerge.fields.webAddress ||
                duplicate.duplicateName === businessConstants.exchangeMerge.fields.socialMedia) {

                duplicate[valueProperty].forEach(item => {
                    const contact = {
                        valid: true,
                        name: item.name
                    };
                    const fieldName = duplicate.duplicateName;
                    if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.email) {
                        contact[businessConstants.exchangeMerge.valueProperty.address] = item.value;
                        contact[businessConstants.exchangeMerge.valueProperty.primary] = item.primary;
                    }
                    if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.webPhone) {
                        contact[businessConstants.exchangeMerge.valueProperty.phone] = item.value;
                    }
                    if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.webAddress) {
                        contact[businessConstants.exchangeMerge.fields.id] = item.id;
                        contact[businessConstants.exchangeMerge.valueProperty.address] = item.value;
                    }
                    if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.socialMedia) {
                        contact[businessConstants.exchangeMerge.valueProperty.link] = item.value;
                    }
                    if (!contactDetail[fieldName]) {
                        contactDetail[fieldName] = [];
                    }
                    if (!ArrayHelperService.arrayContains(contactDetail[fieldName], contact)) {
                        contactDetail[fieldName].push(contact);
                    }
                });
            } else if (duplicate.duplicateName === businessConstants.exchangeMerge.fields.notes) {
                contactDetail[duplicate.duplicateName] = duplicate[valueProperty].map(item => item.name).join(',');
            }
        }
    }

    //#endregion
}
